import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Shield, Award, Users, Menu, X, Star, CheckCircle, Phone, Mail, MapPin } from 'lucide-react';
import './App.css';

const NovaLensesWebsite = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'products', 'about', 'contact'];
      const scrollPosition = window.scrollY + 100;

      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const height = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + height) {
            setActiveSection(section);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const Logo = () => (
    <Link to="/" className="logo-container">
      <img 
        src={require('./NOVA logo icon.png')} 
        alt="Nova Logo" 
        className="logo-image"
        style={{ height: '2.5rem', width: 'auto' }}
      />
      <span className="logo-text">Nova</span>
    </Link>
  );

  return (
    <div className="nova-website">
      {/* Navigation */}
      <nav className="nav-fixed">
        <div className="nav-container">
          <div className="nav-content">
            <Logo />
            
            {/* Desktop Menu */}
            <div className="nav-desktop">
              {[
                { id: 'home', label: 'Home', type: 'scroll' },
                { id: 'about', label: 'About', type: 'scroll' },
                { id: 'instructions', label: 'Instructions', type: 'link' },
                { id: 'contact', label: 'Contact', type: 'scroll' }
              ].map(item => (
                item.type === 'link' ? (
                  <Link
                    key={item.id}
                    to="/instructions"
                    className="nav-button"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`nav-button ${activeSection === item.id ? 'active' : ''}`}
                  >
                    {item.label}
                  </button>
                )
              ))}
            </div>

            {/* Mobile Menu Button */}
            <div className="mobile-menu-button">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="mobile-menu-button"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="mobile-menu">
              <div className="mobile-menu-content">
                {[
                  { id: 'home', label: 'Home', type: 'scroll' },
                  { id: 'about', label: 'About', type: 'scroll' },
                  { id: 'instructions', label: 'Instructions', type: 'link' },
                  { id: 'contact', label: 'Contact', type: 'scroll' }
                ].map(item => (
                  item.type === 'link' ? (
                    <Link
                      key={item.id}
                      to="/instructions"
                      className="mobile-menu-button-item"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="mobile-menu-button-item"
                    >
                      {item.label}
                    </button>
                  )
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-container">
          <div className="hero-grid">
            <div>
              <h1 className="hero-title">
                Clear Vision,
                <span className="hero-gradient-text">
                  {' '}Comfortable
                </span>
                <br />Experience
              </h1>
              <p className="hero-description">
                Premium soft contact lenses designed for sensitive eyes. Experience the perfect blend of comfort, clarity, and quality with Nova lenses.
              </p>
              <div className="hero-buttons">
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="btn-primary"
                >
                  Get in Touch
                </button>
              </div>
            </div>
            <div style={{ position: 'relative' }}>
              <div className="hero-card">
                <div className="hero-card-content">
                  <div className="hero-card-icon">
                    <Eye size={64} style={{ color: '#0d9488' }} />
                  </div>
                  <h3 className="hero-card-title">Nova SN4001</h3>
                  <p className="hero-card-subtitle">Premium Soft Contact Lens</p>
                  <div className="hero-stars">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={20} className="star-icon" />
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ 
                position: 'absolute', 
                top: '-1rem', 
                right: '-1rem', 
                width: '6rem', 
                height: '6rem', 
                background: 'linear-gradient(to bottom right, #2dd4bf, #0d9488)', 
                borderRadius: '50%', 
                opacity: 0.2 
              }}></div>
              <div style={{ 
                position: 'absolute', 
                bottom: '-2rem', 
                left: '-2rem', 
                width: '8rem', 
                height: '8rem', 
                background: 'linear-gradient(to bottom right, #94a3b8, #64748b)', 
                borderRadius: '50%', 
                opacity: 0.1 
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Nova?</h2>
            <p className="section-subtitle">
              Our commitment to excellence ensures every lens meets the highest standards of comfort and quality.
            </p>
          </div>
          
          <div className="features-grid">
            {[
              {
                icon: <Shield style={{ color: '#0d9488' }} size={48} />,
                title: "Safe & Sterile",
                description: "ISO 13485 certified manufacturing with strict quality controls and sterile packaging."
              },
              {
                icon: <Eye style={{ color: '#0d9488' }} size={48} />,
                title: "Sensitive Eye Friendly",
                description: "Specially formulated HEMA material with 42% water content for maximum comfort."
              },
              {
                icon: <Award style={{ color: '#0d9488' }} size={48} />,
                title: "Premium Quality",
                description: "CE marked and regulatory compliant lenses trusted by eye care professionals."
              }
            ].map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* About Section */}
      <section id="about" className="about-section">
        <div className="section-container">
          <div className="about-grid">
            <div>
              <h2 className="section-title">About Nova</h2>
              <div className="about-content">
                <p className="about-description">
                  Nova is dedicated to providing premium contact lenses that combine cutting-edge technology with exceptional comfort. Our lenses are specifically designed for people with sensitive eyes, ensuring a comfortable wearing experience throughout the day.
                </p>
                <p className="about-description">
                  With ISO 13485 certification and CE marking, our products meet the highest international standards for medical devices. We are committed to innovation, quality, and customer satisfaction in everything we do.
                </p>
              </div>
              <div className="stats-grid">
                {[
                  { icon: <Users style={{ color: '#0d9488' }} size={32} />, title: "10K+ Happy Customers" },
                  { icon: <Award style={{ color: '#0d9488' }} size={32} />, title: "ISO 13485 Certified" },
                  { icon: <Shield style={{ color: '#0d9488' }} size={32} />, title: "CE Marked Quality" },
                  { icon: <Eye style={{ color: '#0d9488' }} size={32} />, title: "Sensitive Eye Specialist" }
                ].map((item, index) => (
                  <div key={index} className="stat-item">
                    {item.icon}
                    <span className="stat-text">{item.title}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="about-visual">
                <div className="about-visual-content">
                  <Logo />
                  <p className="about-tagline">Clear Vision, Comfortable Experience</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="contact-header">
            <h2 className="contact-title">Get in Touch</h2>
            <p className="contact-subtitle">Ready to experience Nova lenses? Contact us today.</p>
          </div>
          
          <div className="contact-grid">
            {[
              
              {
                icon: <Mail style={{ color: '#7dd3fc' }} size={32} />,
                title: "Email Us",
                info: "info@novalenses.com",
                description: "We'll respond within 24 hours"
              },
              
            ].map((contact, index) => (
              <div key={index} className="contact-card">
                <div className="contact-icon">{contact.icon}</div>
                <h3 className="contact-card-title">{contact.title}</h3>
                <p className="contact-info">{contact.info}</p>
                <p className="contact-description">{contact.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-left">
              <Logo />
              <p className="footer-tagline">Premium contact lenses for sensitive eyes</p>
            </div>
            <div className="footer-right">
              <p className="copyright">© 2025 Nova Lenses. All rights reserved.</p>
              <p className="certifications">ISO 13485 Certified | CE Marked</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NovaLensesWebsite;